import * as React from 'react';
import { Menu } from '@headlessui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { useMemo } from 'react';

import { cn } from '@restworld/utils-common';
export type MenuSelectorItemProps = {
  label: string | number | React.ReactNode;
  value: string | number;
};

export type PriorityItem = string | number;

export interface MenuSelectorProps {
  options: MenuSelectorItemProps[];
  menuButtonStyles?: string[];
  menuDialogStyles?: string[];
  menuItemStyles?: string[];
  onSelect: (value: string | number) => void;
  selectedLabel?: string | number | React.ReactNode;
  priorityItems?: PriorityItem[];
  background?: string;
  disabled?: boolean;
  testIds?: {
    input?: string;
    dropdownBtn?: string;
    dropdownOptions?: string;
    supportText?: string;
  };
}

export const MenuSelector = ({
  options,
  menuButtonStyles,
  menuDialogStyles,
  menuItemStyles,
  onSelect,
  selectedLabel,
  priorityItems,
  background,
  disabled,
  testIds,
}: MenuSelectorProps) => {
  let menuOptions = options;
  let priorityOptions: MenuSelectorItemProps[] = [];
  if (priorityItems) {
    const priorityItemSet = new Set(priorityItems);
    menuOptions = options.filter((option) => !priorityItemSet.has(option.value));
    const newPriorityOptions: MenuSelectorItemProps[] = [];
    priorityItems.forEach((item) => {
      const option = options.find((option) => option.value === item);
      if (option) {
        newPriorityOptions.push(option);
      }
    });
    priorityOptions = newPriorityOptions;
  }

  const upIcon = useMemo(
    () =>
      background === 'white' ? (
        <ChevronUpIcon className="text-black w-5 h-5" />
      ) : (
        <ChevronUpIcon className="text-white w-5 h-5" />
      ),
    [background]
  );

  const downIcon = useMemo(() => <ChevronDownIcon className="text-black w-5 h-5" />, []);

  return (
    <Menu>
      {/* <div className="relative flex flex-col items-start"> */}
      <StyledMenuButton $menuButtonStyles={menuButtonStyles} disabled={disabled} data-cy={testIds?.dropdownBtn}>
        {({ open }) => (
          <div className="flex flex-row items-center gap-2">
            {!selectedLabel ? <span>Select</span> : selectedLabel}
            {open ? upIcon : downIcon}
          </div>
        )}
      </StyledMenuButton>
      <StyledMenuItems menuDialogStyles={menuDialogStyles} anchor="bottom start" data-cy={testIds?.dropdownOptions}>
        {priorityOptions.length > 0 && (
          <StyledPriorityMenuContainer>
            {priorityOptions?.map(({ label, value }, index) => (
              <Menu.Item key={index}>
                {({ close }) => (
                  <StyledMenuItemContainer
                    menuItemStyles={menuItemStyles}
                    onClick={() => {
                      close();
                      onSelect(value);
                    }}
                  >
                    {label}
                  </StyledMenuItemContainer>
                )}
              </Menu.Item>
            ))}
          </StyledPriorityMenuContainer>
        )}
        <StyledMenuContainer>
          {menuOptions?.map(({ label, value }, index) => (
            <Menu.Item key={index}>
              {({ close }) => (
                <StyledMenuItemContainer
                  menuItemStyles={menuItemStyles}
                  onClick={() => {
                    close();
                    onSelect(value);
                  }}
                >
                  {label}
                </StyledMenuItemContainer>
              )}
            </Menu.Item>
          ))}
        </StyledMenuContainer>
      </StyledMenuItems>
    </Menu>
  );
};

const StyledMenuButton = ({ $menuButtonStyles, className, ...props }: any) => (
  <Menu.Button
    className={cn(
      'flex box-border px-4 py-3 text-base font-normal leading-5 text-black whitespace-nowrap',
      ...($menuButtonStyles || []),
      className
    )}
    {...props}
  />
);

const StyledMenuItems = ({ menuDialogStyles, className, ...props }: any) => (
  <Menu.Items
    className={cn(
      'flex flex-col gap-0 bg-white border border-gray-200 rounded-lg pr-2.25 w-auto',
      ...(menuDialogStyles || []),
      className
    )}
    {...props}
  />
);

const StyledMenuContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-3 p-4', className)} {...props} />
);

const StyledPriorityMenuContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-3 p-4 border-b border-solid border-gray-200', className)} {...props} />
);

const StyledMenuItemContainer = ({ menuItemStyles, className, ...props }: any) => (
  <div className={cn('font-normal text-sm leading-5.25 hover:cursor-pointer px-4 text-black', className)} {...props} />
);
